import type { Team } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { apiErrorMessage } from "@/lib/api/_client";
import { useActiveTenantUsers } from "@/lib/hooks/use-clients";
import { useDeleteTeam } from "@/lib/hooks/use-teams";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./teams-table.i18n";

type TeamsTableProps = {
  teams: Team[];
  onEdit: (team: Team) => void;
};

export function TeamsTable({ teams, onEdit }: TeamsTableProps) {
  const t = useT(T);
  const { tenantUsers } = useActiveTenantUsers();
  const remove = useDeleteTeam();
  const { confirm, ConfirmationDialog } = useConfirm();

  const nameById = new Map(tenantUsers.map((u) => [u.id, u.name]));

  const handleDelete = async (team: Team) => {
    const confirmed = await confirm({
      title: t.deleteTitle,
      description: t.deleteBody(team.name, String(team.clientCount)),
      confirmText: t.delete,
      variant: "destructive",
    });
    if (!confirmed) return;
    try {
      await remove.mutateAsync(team.id);
    } catch (err) {
      await confirm({
        title: t.error,
        description: apiErrorMessage(err, t.failedToDelete),
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  if (teams.length === 0) {
    return <EmptyState>{t.noTeamsYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead>{t.members}</TableHead>
            <TableHead>{t.clients}</TableHead>
            <TableHead className="text-right">{t.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {teams.map((team) => {
            // Members that are no longer active users are not in the lookup; skip them.
            const names = team.memberIds
              .map((id) => nameById.get(id))
              .filter((n): n is string => Boolean(n));
            return (
              <TableRow key={team.id}>
                <TableCell className="font-medium">{team.name}</TableCell>
                <TableCell className="max-w-md truncate">
                  {names.length > 0 ? names.join(", ") : t.noMembers}
                </TableCell>
                <TableCell>{team.clientCount}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(team)}
                      tooltip={t.edit}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(team)}
                      loading={remove.isPending && remove.variables === team.id}
                      tooltip={t.delete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ConfirmationDialog />
    </>
  );
}
